import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { isSafeToken, isTrustedRedirect, tokenFromHistoryState, tokenFromParams, tokenToFragment } from 'app/modules/shared/utils/third-party-data';
import { TunstallEmergencyListService } from './tunstall-emergency-list.service';
import { ActivatedRoute, Router } from '@angular/router';
import { DatePipe, NgClass } from '@angular/common';
import { TranslocoModule } from '@jsverse/transloco';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'jhi-tunstall-emergency-list',
    templateUrl: './tunstall-emergency-list.component.html',
    styleUrls: ['./tunstall-emergency-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [TranslocoModule, MatTableModule, NgClass, DatePipe]
})
export class TunstallEmergencyListComponent implements OnInit, OnDestroy {
    // lastUmoPstnFallbackList: UserAndEmergencyDTO[] | null = [];
    isLoading = false;
    token = '-1';

    displayedColumns: string[] = [
        'startedAt',
        'reason',
        // 'apNumber',
        "externalId",
        'salutation',
        // 'title',
        // 'nickName',
        'firstName',
        'lastName',
    ];

    // displayedColumnsData = {
    //     startedAt: 'startedAt',
    //     apNumber: 'apNumber',
    //     salutation: 'salutation',
    //     // title: 'title',
    //     // nickName: 'nickName',
    //     firstName: 'firstName',
    //     lastName: 'lastName',
    //     reason: 'reason',
    // };

    dataSource: MatTableDataSource<any>;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private tunstallEmergencyListService: TunstallEmergencyListService
    ) {
        this.dataSource = new MatTableDataSource([]);
    }

    ngOnInit(): void {
        this.activatedRoute.queryParams
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((params) => {
                // new links carry the token in the fragment ("#eaf="), which no server receives; earlier ones in the query
                const linkToken = tokenFromParams(this.activatedRoute.snapshot.fragment, 'eaf') ?? params.eaf;
                // after a reload the address no longer has it; the history entry of this tab does
                const token = linkToken ?? tokenFromHistoryState(window.history.state, 'eaf');
                if (!isSafeToken(token)) {
                    // missing or malformed token from the link (BSI O.Source_1)
                    this.router.navigate(['404']);
                } else if (token !== this.token) {
                    this.token = token;
                    // the token leaves the address bar, so that it is not on the screen and not in the browser history
                    // (BSI O.Source_11); the state of the history entry keeps it for a reload of this page in this tab
                    if (linkToken !== undefined) {
                        this.router.navigate([], {
                            relativeTo: this.activatedRoute,
                            queryParams: { eaf: null },
                            queryParamsHandling: 'merge',
                            replaceUrl: true,
                            state: { eaf: token },
                        });
                    }

                    this.tunstallEmergencyListService
                        .findLastTunstallEmergencyList(token)
                        .pipe(takeUntil(this._unsubscribeAll))
                        .subscribe((received) => {
                            this.isLoading = false;
                            // this.dataSource = new MatTableDataSource(dummyData);
                            received.body?.sort(
                                (a, b) =>
                                    new Date(b.startedAt).getTime() -
                                    new Date(a.startedAt).getTime()
                            );
                            this.dataSource = new MatTableDataSource(
                                received.body
                            );
                        });
                }
            });
    }

    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    redirect(urlAgentAccessToken: string) {
        // Redirect to the new web app
        const envMapping: { [key: string]: string } = {
            'https://repository.test.almaphil.net':
                'https://adele.test.almaphil.de',
            'https://repository.dev.almaphil.net':
                'https://adele.dev.almaphil.de',
            'https://repository.prod.almaphil.net': 'https://adele.almaphil.de',
        };

        const matchedEnv = Object.keys(envMapping).find((env) =>
            urlAgentAccessToken.includes(env)
        );

        if (matchedEnv) {
            let newUrl = urlAgentAccessToken.replace(
                matchedEnv,
                envMapping[matchedEnv]
            );
            if (window.location.origin.includes('http://localhost:4200')) {
                newUrl = urlAgentAccessToken.replace(
                    matchedEnv,
                    'http://localhost:4200'
                );
            }
            // the agent access token goes in the fragment, so that the request for the page does not carry it
            newUrl = tokenToFragment(newUrl, 'eaa');
            if (isTrustedRedirect(newUrl, window.location.origin)) {
                window.location.href = newUrl;
            }
        }
    }

    openInNewTab(url?: string): void {
        if (url) {
            window.open(url, '_blank');
        } else {
            console.error('Invalid URL');
        }
    }
}
